import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { DateTime } from 'luxon';
import type { ChancenRole } from '../../semantics/ChancenRole.js';
import { SemanticRelationship } from '../../semantics/SemanticRelationship.js';
import type { ChancenwerkUser } from './entities/ChancenwerkUser.js';

const REBUILD_BATCH_SIZE = 200;

/**
 * This function is a server-side function only. It takes userIDs and rebuild all permissions based on the semantic relationships the users have
 * @param userIDs The userIDs for which the function should rebuild the permissions for
 */
export async function rebuildPermissionsForUsers(userIDs: string[]) {
  // Process in batches to avoid loading too many users with expanded $lookup data at once
  let totalUpdated = 0;
  for (let i = 0; i < userIDs.length; i += REBUILD_BATCH_SIZE) {
    const batchIDs = userIDs.slice(i, i + REBUILD_BATCH_SIZE);
    totalUpdated += await _rebuildPermissionsBatch(batchIDs);
  }
  return totalUpdated;
}

async function _rebuildPermissionsBatch(userIDs: string[]) {
  // Get the user from the database
  const userCollection = await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName);
  const users = userCollection.aggregate<
    EntityData<
      ChancenwerkUser & {
        relationships: Array<EntityData<SemanticRelationship> & { predicate: Array<EntityData<ChancenRole>> }>;
      }
    >
  >([
    { $match: { _id: { $in: userIDs } } },
    {
      $lookup: {
        from: SemanticRelationship._collectionName,
        as: 'relationships',
        localField: '_id',
        foreignField: 'subjectID',
        pipeline: [
          {
            $lookup: {
              from: 'adornisroles',
              as: 'predicates',
              localField: 'predicateID',
              foreignField: '_id',
            },
          },
          { $addFields: { predicate: { $arrayElemAt: ['$predicates', 0] } } },
        ],
      },
    },
    { $project: { relationships: 1, roles: 1, _class: 1, kuendigung: 1 } },
  ]);

  const rolesOnUsersToUpdate: Array<{
    userID: string;
    roles: Array<{
      _id: string;
      _class: string;
      name: string;
      contexts: string[];
    }>;
  }> = [];

  for await (const user of users) {
    // Update the roles of the user
    const roles: Array<{
      _id: string;
      _class: string;
      name: string;
      contexts: string[];
    }> = [];

    for (const relationship of user.relationships.map(r => constructValue(r) as SemanticRelationship)) {
      if (relationship._class === 'StudentParticipationRelationship' && user._class === 'Student') {
        const student = user as unknown as { kuendigung: Maybe<{ austrittsdatum: Date | null }> };
        if (
          student.kuendigung?.austrittsdatum &&
          DateTime.fromJSDate(student.kuendigung.austrittsdatum) > DateTime.now()
        ) {
          continue;
        }
      } else {
        if (!relationship.isActive()) continue;
      }

      const predicate = relationship.predicate;
      if (!predicate) continue;

      // Search a already existing role to add the object as the context. If none is found create a new one from the predicate.
      const role = roles.find(w => w.name === predicate.name);
      if (role) {
        role.contexts.push(relationship.getContext());
      } else {
        roles.push({
          _id: A.getGloballyUniqueID(),
          _class: AdornisRoleToContextsWrapper._class,
          name: predicate.name,
          contexts: [relationship.getContext()],
        });
      }
    }

    // Check if a role has changed and update is needed
    if (
      roles.length !== user.roles.length ||
      !roles.every(r =>
        user.roles.some(
          ur =>
            ur.name === r.name &&
            ur.contexts.length === r.contexts.length &&
            ur.contexts.every(c => r.contexts.includes(c)),
        ),
      )
    ) {
      logger.info(
        {
          oldRoles: user.roles,
          rolesOnUsersToUpdate,
        },
        `Rebuilding permissions for user ${user._id}`,
      );
      rolesOnUsersToUpdate.push({
        userID: user._id,
        roles,
      });
    }
  }

  if (rolesOnUsersToUpdate.length)
    await userCollection.bulkWrite(
      // @ts-expect-error The Objects dont need a permission array
      rolesOnUsersToUpdate.map(operation => ({
        updateOne: { filter: { _id: operation.userID }, update: { $set: { roles: operation.roles } } },
      })),
    );

  return rolesOnUsersToUpdate.length;
}
